'use client';

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@pickleballinc/react-ui';
import { useEffect, useRef, useState } from 'react';

interface DropdownProps {
	options: { value: string; label: string; isDisabled?: boolean; teamMaxRating?: number }[];
	placeholder?: string;
	onSelect: (value: string) => void;
	menuListClassName?: string;
	error?: boolean;
	errorMessage?: string;
	value?: string;
	showAll?: boolean;
	menuListStyle?: React.CSSProperties;
	className?: string;
}

export const Dropdown = ({
	options,
	onSelect,
	placeholder,
	error,
	errorMessage,
	menuListClassName,
	value,
	showAll = true,
	menuListStyle,
	className = ''
}: DropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const toggleDropdown = () => setIsOpen((prev) => !prev);

	const handleSelect = (selectedValue: string) => {
		onSelect(selectedValue);
		setIsOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const selectedLabel =
		options.length === 0
			? 'No options available'
			: value && options.find((option) => option.value === value)?.label
				? options.find((option) => option.value === value)?.label
				: placeholder || 'Select an option';

	return (
		<div ref={dropdownRef} className="relative" role="combobox" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls="dropdown-list">
			<Menu showArrow={false} offset={5}>
				<Menu.Trigger asChild>
					<button
						onClick={toggleDropdown}
						disabled={!!error || options.length === 0}
						aria-label={selectedLabel}
						className={`flex w-full items-center justify-between rounded-md border px-4 py-2 text-sm font-normal shadow-sm focus:outline-none ${className} ${
							error ? 'text-red-700 border-error-500' : 'border-gray-300 bg-white text-gray-900'
						} ${options.length === 0 ? 'opacity-50' : ''}`}
					>
						{selectedLabel}
						<FontAwesomeIcon
							icon={faAngleDown}
							className={`ml-2 size-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
							aria-hidden="true"
						/>
					</button>
				</Menu.Trigger>

				<Menu.List
					className={`${menuListClassName} !max-h-60 overflow-y-auto`}
					style={menuListStyle}
					id="dropdown-list"
					role="listbox"
					aria-label="Options"
				>
					{options.length === 0 ? (
						<Menu.Item
							key="no-options"
							role="option"
							aria-disabled="true"
							className="cursor-not-allowed rounded-md px-4 py-2 text-sm font-medium text-gray-500"
						>
							No options available
						</Menu.Item>
					) : (
						<>
							{showAll && placeholder ? (
								<Menu.Item
									key="reset"
									onClick={() => handleSelect('')}
									role="option"
									aria-selected={value === ''}
									className={`cursor-pointer rounded-md px-4 py-2 text-sm font-medium text-gray-900 ${
										value === '' ? 'bg-[#FCD3B0]' : 'hover:bg-[#FEE8D2]'
									}`}
								>
									{placeholder}
								</Menu.Item>
							) : null}
							{options.map((option) => (
								<Menu.Item
									key={option.value}
									onClick={() => handleSelect(option.value)}
									role="option"
									aria-selected={value === option.value}
									className={`rounded-md px-4 py-2 text-sm font-medium ${
										option.isDisabled
											? 'text-gray-400 pointer-events-none'
											: `text-gray-900 cursor-pointer ${value === option.value ? 'bg-[#FCD3B0]' : 'hover:bg-[#FEE8D2]'}`
									}`}
								>
									{option.label}
									{option.teamMaxRating ? <span className="pl-1">(team rating {option.teamMaxRating})</span> : null}
								</Menu.Item>
							))}
						</>
					)}
				</Menu.List>
			</Menu>
			{errorMessage ? (
				<p className="pr-2 pt-2 text-sm text-error-500" role="alert" aria-live="polite">
					{errorMessage}
				</p>
			) : null}
		</div>
	);
};
